<script setup lang="ts">
  import {
    Kbd,
    SidebarMenuButton,
    navigatePreservingBrowsingContext,
    useAppShellSectionMenu,
    useSidebar
  } from '@gat-ship/vue-components'
  import { useGlobalSearchDialog } from '@gat-ship/global-search'
  import { useFeatureFlag } from '@gat-ship/feature-flag'
  import { useUser } from '@gat-ship/user'
  import { accessCodes } from '@gat-ship/types'
  import {
    IconStakeholders,
    IconChevronRight,
    IconMail,
    IconSearch,
    IconPortcall
  } from '@gat-ship/icons'

  const user = useUser()

  const { openSearchCommand } = useGlobalSearchDialog()
  const featureFlag = useFeatureFlag()

  // In the mobile Sheet the active section's nav item is redundant —
  // its menu is the other toggle panel (same rule as the app sidebar's
  // Administration item).
  const { isMobile } = useSidebar()
  const sectionMenu = useAppShellSectionMenu(null)
</script>

<template>
  <SidebarMenuButton
    variant="nav"
    tooltip="Search"
    class="cursor-pointer"
    @click="openSearchCommand"
  >
    <icon-search class="lucide-icon shrink-0" />
    <div
      class="flex grow items-center gap-2 group-data-[collapsible=icon]:hidden"
    >
      <div class="flex-1 text-sm">Search</div>
      <Kbd>ctrl + k</Kbd>
    </div>
  </SidebarMenuButton>
  <router-link
    v-slot="{ href, navigate, isActive, isExactActive }"
    custom
    :to="{ name: 'portcalls.overview' }"
  >
    <SidebarMenuButton
      as="a"
      :href="href"
      variant="nav"
      tooltip="Port Calls"
      :is-active="isActive"
      :data-exact-active="isExactActive"
      @click="navigatePreservingBrowsingContext($event, href, navigate)"
      @auxclick="navigatePreservingBrowsingContext($event, href, navigate)"
    >
      <icon-portcall class="lucide-icon shrink-0" />
      <div
        class="flex grow items-center gap-2 group-data-[collapsible=icon]:hidden"
      >
        <div class="flex-1 text-sm">Port Calls</div>
        <icon-chevron-right
          v-if="isActive"
          class="lucide-icon size-4 shrink-0"
        />
      </div>
    </SidebarMenuButton>
  </router-link>
  <router-link
    v-if="user.hasPermission(accessCodes.system.stakeholder.accessStakeholders)"
    v-slot="{ href, navigate, isActive, isExactActive }"
    custom
    :to="{ name: 'stakeholders.overview' }"
  >
    <SidebarMenuButton
      as="a"
      :href="href"
      variant="nav"
      tooltip="Stakeholders"
      :is-active="isActive"
      :data-exact-active="isExactActive"
      @click="navigatePreservingBrowsingContext($event, href, navigate)"
      @auxclick="navigatePreservingBrowsingContext($event, href, navigate)"
    >
      <icon-stakeholders class="lucide-icon shrink-0" />
      <div
        class="flex grow items-center gap-2 group-data-[collapsible=icon]:hidden"
      >
        <div class="flex-1 text-sm">Stakeholders</div>
        <icon-chevron-right
          v-if="isActive"
          class="lucide-icon size-4 shrink-0"
        />
      </div>
    </SidebarMenuButton>
  </router-link>

  <router-link
    v-if="featureFlag.hasFeatureFlag('mail-prototype')"
    v-slot="{ href, navigate, isActive, isExactActive }"
    custom
    :to="{ name: 'mail-inbox' }"
  >
    <SidebarMenuButton
      v-if="!(isMobile && sectionMenu && isActive)"
      as="a"
      :href="href"
      variant="nav"
      tooltip="Mail Prototype"
      :is-active="isActive"
      :data-exact-active="isExactActive"
      @click="navigatePreservingBrowsingContext($event, href, navigate)"
      @auxclick="navigatePreservingBrowsingContext($event, href, navigate)"
    >
      <icon-mail class="lucide-icon shrink-0" />
      <div
        class="flex grow items-center gap-2 group-data-[collapsible=icon]:hidden"
      >
        <div class="flex-1 text-sm">Mail Prototype</div>
        <icon-chevron-right
          v-if="isActive"
          class="lucide-icon size-4 shrink-0"
        />
      </div>
    </SidebarMenuButton>
  </router-link>
</template>
